import { CoverageType } from '../../../../../models/insuranceCoverage'
import { type InsuranceCoverage, type InsuranceOneTimeFee } from '../../../../../models/insuranceTypes'

export interface CoverageFormData {
  id?: string
  coverageType: string
  vendorCoverageType: string
  premium: number | null
  limit: number | null
  deductible: number | null
  /** Physical damage only: the comprehensive deductible when it differs from collision. */
  comprehensiveDeductible: number | null
  premiumPerUnit: number | null
  /** The carrier's number for the policy this line is written on; Corgi issues one per line. */
  vendorPolicyId: string
  isNew?: boolean
}

export interface FeeFormData {
  id?: string
  label: string
  amount: string
  isNew?: boolean
}

export const COVERAGE_TYPE_OPTIONS: { value: CoverageType; label: string }[] = [
  { value: CoverageType.AUTO_LIABILITY, label: 'Auto Liability' },
  { value: CoverageType.AUTO_PHYSICAL_DAMAGE, label: 'Physical Damage' },
  { value: CoverageType.GENERAL_LIABILITY, label: 'General Liability' },
  { value: CoverageType.MOTOR_TRUCK_CARGO, label: 'Cargo' },
]

/** Staff record contractors' Workers Comp and tools quotes and policies; customers can't upload one yet. */
export const STAFF_COVERAGE_TYPE_OPTIONS = [
  ...COVERAGE_TYPE_OPTIONS,
  { value: CoverageType.WORKERS_COMPENSATION, label: 'Workers Comp' },
  { value: CoverageType.PET, label: 'Pet insurance' },
  { value: CoverageType.TOOLS_AND_EQUIPMENT, label: 'Tools and Equipment' },
]

export function coverageToFormData(coverage: InsuranceCoverage): CoverageFormData {
  return {
    id: coverage.id,
    coverageType: coverage.coverageType,
    vendorCoverageType: coverage.vendorCoverageType,
    premium: coverage.premium,
    limit: coverage.limit,
    deductible: coverage.deductible,
    comprehensiveDeductible: coverage.comprehensiveDeductible ?? null,
    premiumPerUnit: coverage.premiumPerUnit,
    vendorPolicyId: coverage.vendorPolicyId ?? '',
  }
}

export function feeToFormData(fee: InsuranceOneTimeFee): FeeFormData {
  return { id: fee.id, label: fee.label, amount: String(fee.amount) }
}

export function newCoverage(): CoverageFormData {
  return {
    isNew: true,
    coverageType: CoverageType.AUTO_LIABILITY,
    vendorCoverageType: '',
    premium: null,
    limit: null,
    deductible: null,
    comprehensiveDeductible: null,
    premiumPerUnit: null,
    vendorPolicyId: '',
  }
}

/** Sum of coverage premiums + one-time fees, matching the backend `premium()`. */
export function computeTotalPremium(coverages: CoverageFormData[], fees: FeeFormData[]): number {
  const coveragesTotal = coverages.reduce((sum, c) => sum + (parseFloat(String(c.premium ?? 0)) || 0), 0)
  const feesTotal = fees.reduce((sum, f) => sum + (parseFloat(f.amount) || 0), 0)
  return coveragesTotal + feesTotal
}

/** A term the form holds as a number or an API decimal string, kept null when absent.
 *  `|| null` would read a genuine $0 term — "no deductible" — as one we never had. */
export function serializeTerm(value: number | string | null, whole = true): number | null {
  if (value == null || value === '') return null
  const parsed = whole ? parseInt(String(value), 10) : parseFloat(String(value))
  return Number.isNaN(parsed) ? null : parsed
}

/** API payload for the coverages list (used by both the indication + policy forms). */
export function serializeCoverages(coverages: CoverageFormData[]) {
  return coverages.map((c) => ({
    id: c.isNew ? undefined : c.id,
    coverageType: c.coverageType,
    vendorCoverageType: c.vendorCoverageType,
    // Blank stays null: a $0 premium would claim a price nobody entered.
    premium: serializeTerm(c.premium, false),
    limit: serializeTerm(c.limit),
    deductible: serializeTerm(c.deductible),
    comprehensiveDeductible: serializeTerm(c.comprehensiveDeductible),
    premiumPerUnit: serializeTerm(c.premiumPerUnit, false),
    vendorPolicyId: c.vendorPolicyId.trim(),
  }))
}

export function serializeFees(fees: FeeFormData[]) {
  return fees.map((f) => ({
    id: f.isNew ? undefined : f.id,
    label: f.label,
    amount: parseFloat(f.amount) || 0,
  }))
}
